import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { ManagementFormLayout } from 'components/ManagementFormLayout'
import { useUnsavedChangesGuard } from 'components/OrganizationScope/context'
import { routes } from 'constants/routes'
import { useOrgPath } from 'hooks/useOrgPath'
import { commonStrings, lineStrings } from 'constants/strings'
import { useRegisterLineChannel } from 'hooks'

export default function LineConnect() {
  const navigate = useNavigate()
  const orgPathFor = useOrgPath()
  const strings = lineStrings.connect

  const [basicId, setBasicId] = useState('')
  const [channelId, setChannelId] = useState('')
  const [channelSecret, setChannelSecret] = useState('')
  const [validationError, setValidationError] = useState<string | undefined>()
  const markSaved = useUnsavedChangesGuard(
    JSON.stringify({ basicId, channelId, channelSecret }),
  )
  const { register, isSubmitting, error: apiError } = useRegisterLineChannel({
    onSuccess: (response) => {
      markSaved()
      if (response.statusAdjustedToPaused) {
        const message = response.webhookMessage || lineStrings.connect.warnings.webhookPaused
        navigate(orgPathFor(routes.LINE), { replace: true, state: { snackbarMessage: message, snackbarVariant: 'error' } })
      } else {
        navigate(orgPathFor(routes.LINE), { replace: true })
      }
    },
  })
  const error = validationError || apiError

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isSubmitting) return

    setValidationError(undefined)

    const normalizedBasicId = basicId.trim().replace(/^@+/, '')
    const trimmedChannelId = channelId.trim()
    const trimmedChannelSecret = channelSecret.trim()

    if (!normalizedBasicId || !trimmedChannelId || !trimmedChannelSecret) {
      setValidationError(strings.errors.required)
      return
    }

    register({
      basicId: normalizedBasicId,
      channelId: trimmedChannelId,
      channelSecret: trimmedChannelSecret,
    })
  }

  return (
    <ManagementFormLayout
      kicker={strings.kicker}
      title={strings.title}
      description={strings.description}
      backTo={orgPathFor(routes.LINE)}
    >
      <form
        className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-1">
          <h2 className="text-sm font-semibold text-ink">
            {strings.form.title}
          </h2>
          <p className="text-xs text-muted">{strings.form.description}</p>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4">
          {lineStrings.providerNotice.map((line, i) => (
            <p key={i} className="text-sm text-amber-800">{line}</p>
          ))}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.basicId.label}
            <div className="flex items-stretch">
              <span className="inline-flex items-center rounded-l-2xl border border-border/80 border-r-0 bg-white/80 px-3 text-sm text-muted/70 shadow-sm">
                @
              </span>
              <Input
                name="basicId"
                placeholder={strings.form.fields.basicId.placeholder}
                autoComplete="off"
                className="rounded-l-none border-l-0"
                required
                disabled={isSubmitting}
                value={basicId}
                onChange={(event) => setBasicId(event.target.value)}
              />
            </div>
          </label>
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.channelId.label}
            <Input
              name="channelId"
              placeholder={strings.form.fields.channelId.placeholder}
              autoComplete="off"
              required
              disabled={isSubmitting}
              value={channelId}
              onChange={(event) => setChannelId(event.target.value)}
            />
          </label>
          <label className="grid gap-2 text-xs font-semibold text-ink sm:col-span-2">
            {strings.form.fields.channelSecret.label}
            {/* 長いシークレットを途中で見切れさせず全文を折り返し表示する */}
            <textarea
              name="channelSecret"
              placeholder={strings.form.fields.channelSecret.placeholder}
              autoComplete="off"
              required
              rows={2}
              disabled={isSubmitting}
              value={channelSecret}
              onChange={(event) => setChannelSecret(event.target.value)}
              className="w-full resize-none break-all rounded-2xl border border-border/80 bg-white/80 px-3 py-2.5 font-mono text-sm leading-relaxed text-ink placeholder:font-sans placeholder:text-muted/70 shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-70"
            />
          </label>
        </div>
        {error && <Alert>{error}</Alert>}
        <div className="flex justify-end">
          <Button size="md" type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? `${strings.actions.submit}${commonStrings.loadingEllipsis}`
              : strings.actions.submit}
          </Button>
        </div>
      </form>
    </ManagementFormLayout>
  )
}
